import { Tooltip } from "react-tooltip";
import clsx from "clsx";
import { settingsIcon } from "@/components/icons/SVGIcons";
import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { toggleTradeSettingsPanel } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/toggleTradeSettingsPanel";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import requestInformationStore from "@/store/requestInformationStore";
import { selectEsHasPendingRequest } from "@/store/requestInformationStoreACTIONS";

// LQC-1301 — single settings cog on each ES blotter row. Clicking it opens the
// left-rail Trade Settings panel (TradeSettingsSidebarContent) targeted at this
// trade; if the panel is already open it just retargets it to this row.
//
// LQC-637/LQC-1744 — the cog PULSES on the OP's row when an RP has requested a
// piece of information (Direction / Comp / Cover / Cover Spread) and the OP hasn't
// yet responded. Only the OP sees it: the RP made the request and is waiting, so
// its own row never pulses.

// Pure: the OP's settings cog pulses to flag an RP request awaiting the OP's
// response. Exported for unit testing.
export function shouldPulseSettingsCog(
  isOpRow: boolean,
  hasPendingRequest: boolean,
): boolean {
  return isOpRow && hasPendingRequest;
}

const TradeWideSettingsCellRenderer = (params: any) => {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid: string | undefined = fullTradeData?.esid;
  const cryptoID: string | undefined = params.cryptoID;

  // Reactive: does any execution request on this ES have an RP request the OP
  // hasn't actioned? Drives the pulse (gated to OP rows below).
  const hasPendingRequest = requestInformationStore((state) =>
    esid ? selectEsHasPendingRequest(esid)(state) : false,
  );

  // Reactive: keep the cog solid orange while this trade's Trade Settings panel
  // is the one currently open (keyed by cryptoID so multiple blotters don't clash).
  const isPanelOpenOnThisTrade = tradeSettingsStore((state) =>
    cryptoID ? state.panelSelectedEsidByCryptoId[cryptoID] === esid : false,
  );

  // Shown for both OP and RP rows — the panel renders the right perspective
  // (editable OP settings vs read-only RP values) from the selected trade.
  if (!esid) {
    return (
      <div
        data-testid="TradeWideSettingsCellEmpty"
        id="TradeWideSettingsCellEmpty"
      />
    );
  }

  const showRequestPulse = shouldPulseSettingsCog(
    fullTradeData?.role === "OP",
    hasPendingRequest,
  );
  const tooltipId = `tradeWideSettings-${esid}`;

  const togglePanel = () => {
    toggleTradeSettingsPanel({ api: params.api, cryptoID, esid });
  };

  return (
    <div
      data-testid="TradeWideSettingsCellContainer"
      id="TradeWideSettingsCellContainer"
      className="flex h-full w-full items-center justify-center"
    >
      <span
        data-testid="TradeWideSettingsIconButton"
        id={`TradeWideSettingsIconButton-${esid}`}
        data-tooltip-id={tooltipId}
        data-tooltip-content={
          showRequestPulse
            ? "RP has requested information — click to respond"
            : "Click to open settings"
        }
        aria-label="Open trade settings"
        role="button"
        className={clsx(
          "inline-flex cursor-pointer items-center justify-center transition-all [&>svg]:h-[18px] [&>svg]:w-[18px]",
          // Dull grey by default, full opacity on row hover; the cog itself goes
          // orange when hovered directly. The open panel's trade stays solid
          // orange (no pulse) so it reads as "selected".
          //
          // LQC-637/LQC-1744: a pending RP request takes priority — the cog
          // pulses orange so the OP notices it needs a response, even when the
          // panel for this trade is already open.
          showRequestPulse && !isPanelOpenOnThisTrade
            ? "animate-pulse text-orange-500"
            : isPanelOpenOnThisTrade
              ? "text-orange-500 opacity-100"
              : "text-gray-500 opacity-40 hover:text-orange-500 hover:opacity-100 dark:text-gray-400 dark:hover:text-orange-500 dark:hover:opacity-100 [.ag-row-hover_&]:opacity-100",
        )}
        onClick={togglePanel}
      >
        {settingsIcon}
      </span>
      <Tooltip id={tooltipId} place="left" className="z-50 !text-xs" />
    </div>
  );
};

export default TradeWideSettingsCellRenderer;
